<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import {
  CategoryStorePayload,
  CategoryStoreResponse,
} from "@/types/interface/category-store";
import { useCategoryStore } from "@/stores/master/category-store";

const idCategoryStore = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const categoryStore = useCategoryStore();
const detailCategoryStore = ref<CategoryStoreResponse>();

const validationSchema = toTypedSchema(
  zod.object({
    code: zod.string().nonempty({ message: "Code is required" }),
    name: zod.string().nonempty({ message: "Category Store Name is required" }),
    description: zod.string().nonempty({ message: "Description is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: code } = useField<string>("code");
const { value: name } = useField<string>("name");
const { value: description } = useField<string>("description");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadCategoryStore: CategoryStorePayload = {
    code: code.value,
    name: name.value,
    description: description.value,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await categoryStore.dispatchUpdate(
      idCategoryStore.value,
      payloadCategoryStore
    );
  } else {
    responseApi.value = await categoryStore.dispatchCreate(
      payloadCategoryStore
    );
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    toast.success(
      isEdit.value
        ? "Category Store Successfully Updated"
        : "Category Store Successfully Added"
    );
    setTimeout(() => {
      isLoading.value = false;
      router.push("/master/category-store");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailCatgeoryStore = async () => {
  const { success, message } = await categoryStore.dispatchDetail(
    idCategoryStore.value
  );
  if (success) {
    detailCategoryStore.value = await categoryStore.category?.data;
    name.value = detailCategoryStore.value?.name;
    code.value = detailCategoryStore.value?.code;
    description.value = detailCategoryStore.value?.description;
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idCategoryStore.value = router.currentRoute.value.params.id.toString();
    loadDetailCatgeoryStore();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Category Store</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/category-store"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Category Store
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="code" class="form-label label text-secondary"
                    >Code <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="code"
                      v-model="code"
                      placeholder="Category Store Code"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Category Store Name
                    <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Category Store Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="description"
                    class="form-label label text-secondary"
                    >Description</label
                  >
                  <div class="position-relative">
                    <textarea
                      name="description"
                      v-model="description"
                      placeholder="Description"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.description }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
